import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, firestore, storage } from '../../../firebase.js';
import { useParams, useNavigate } from 'react-router-dom';
import { TextField, Button, Container, CircularProgress, Typography, Box } from '@mui/material';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

function EditForm() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [isEditable, setIsEditable] = useState(false);
  const [user, setUser] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();

  const isPrivilegedRole = (role) => {
    const normalized = String(role || '').trim().toLowerCase();
    return [
      'admin',
      'superadmin',
      'seller',
      'facility_admin',
      'organisation_admin',
      'ou admin',
      'ou operator',
      'organisation_operator',
      'ou user',
      'operator',
      'user',
      'shop',
    ].includes(normalized);
  };

  // First useEffect to handle authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || null);
      setAuthInitialized(true);
    });
    
    // Cleanup subscription
    return () => unsubscribe();
  }, [auth]);

  // Second useEffect to fetch item data, separate from authentication
  useEffect(() => {
    const fetchItem = async () => {
      if (!id) return;
      if (!authInitialized) {
        return;
      }

      if (!user) {
        setLoading(false);
        setError('Please sign in to view this item.');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        const docRef = doc(firestore, 'ItemMaster', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const itemData = docSnap.data();
          const currentRole = sessionStorage.getItem('userRole') || '';
          if (!isPrivilegedRole(currentRole) && itemData.userId && itemData.userId !== user.uid) {
            setItem(null);
            setError('Access denied. You can only view your own items.');
          } else {
            setItem(itemData);
            setError(null);
          }
        } else {
          setItem(null);
          setError('No item found.');
        }
      } catch (error) {
        console.error("Error fetching document: ", error);
        setError('Error fetching document.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchItem();
  }, [authInitialized, id, user]);

  useEffect(() => {
    setImagePreviewUrl(item?.imageUrl || '');
  }, [item]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setItem((prevItem) => ({
      ...prevItem,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!item.itemMake || !item.model || !item.purchaseDate || !item.customerName || !item.productName || !item.warrantyExpiryDate) {
      setValidationError('All fields are required.');
      return;
    }


    try {
      const currentRole = sessionStorage.getItem('userRole') || '';
      if (!isPrivilegedRole(currentRole) && item?.userId && user?.uid && item.userId !== user.uid) {
        setError('Access denied. You can only update your own items.');
        return;
      }

      let nextItem = { ...item };
      if (selectedImage) {
        setImageUploading(true);
        const uploadedImageUrl = await uploadImageWithFallback(selectedImage);
        nextItem = {
          ...nextItem,
          imageUrl: uploadedImageUrl,
        };
      }

      const docRef = doc(firestore, 'ItemMaster', id);
      await updateDoc(docRef, nextItem);
      setItem(nextItem);
      setImagePreviewUrl(nextItem.imageUrl || '');
      setSelectedImage(null);
      setIsEditable(false);
      setImageError('');
      setValidationError('');
    } catch (error) {
      console.error("Error updating document: ", error);
      setError('Error updating document.');
    } finally {
      setImageUploading(false);
    }
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const uploadImageWithFallback = async (file) => {
    try {
      const uniqueImageName = `${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `images/${uniqueImageName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      const uploadedImageUrl = await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          () => {},
          (uploadError) => reject(uploadError),
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            } catch (downloadError) {
              reject(downloadError);
            }
          }
        );
      });

      return uploadedImageUrl;
    } catch (uploadError) {
      if (uploadError?.code === 'storage/quota-exceeded') {
        setImageError('Storage quota exceeded. Saved image using inline fallback.');
        return fileToDataUrl(file);
      }
      throw uploadError;
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setImageError('Only JPG, JPEG, and PNG images are allowed.');
      return;
    }

    setImageError('');
    setSelectedImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleEdit = () => {
    setIsEditable(true);
  };

  const handleCancel = () => {
    setIsEditable(false);
    setValidationError('');
    setImageError('');
    setSelectedImage(null);
    setImagePreviewUrl(item?.imageUrl || '');
    const fetchItem = async () => {
      try {
        const docRef = doc(firestore, 'ItemMaster', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setItem(docSnap.data());
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching document: ", error);
        setError('Error fetching document.');
      }
    };
    fetchItem();
  };

  const handleBack = () => {
    navigate('/warranty_list');
  };

  if (loading) {
    return (
      <Container>
        <Typography variant="h6">Loading...</Typography>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography variant="h6" color="error">{error}</Typography>
      </Container>
    );
  }

  if (!item) {
    return (
      <Container>
        <Typography variant="h6">No item found</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/warranty_list')}
          style={{ marginTop: '16px' }}
        >
          Back to Warranty List
        </Button>
      </Container>
    );
  }

  const textFieldStyle = {
    pointerEvents: !isEditable ? 'none' : 'auto',
    cursor: !isEditable ? 'default' : 'text',
    border: !isEditable ? 'none' : '1px',
    backgroundColor: !isEditable ? 'transparent' : 'transparent',
  };

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom paddingTop={5}>View and Edit Inventory Item</Typography>
        <div>
          {!isEditable ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEdit}
                style={{ marginRight: '8px' }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleBack}
              >
                Back
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                style={{ marginRight: '8px' }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
      {validationError && <Typography variant="h6" color="error">{validationError}</Typography>}
      {imageError && <Typography variant="body2" color="error">{imageError}</Typography>}

      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Product Image</Typography>
        {imagePreviewUrl ? (
          <Box
            component="img"
            src={imagePreviewUrl}
            alt={item.productName || item.itemMake || 'Product image'}
            sx={{
              width: 220,
              height: 150,
              objectFit: 'contain',
              border: '1px solid #ddd',
              borderRadius: 1,
              bgcolor: '#f7f7f7',
              p: 1,
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">No image uploaded</Typography>
        )}

        {isEditable && (
          <Button
            variant="outlined"
            component="label"
            sx={{ mt: 1.5 }}
            disabled={imageUploading}
          >
            {imageUploading ? 'Uploading...' : 'Upload Image'}
            <input
              hidden
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleImageChange}
            />
          </Button>
        )}
      </Box>

      <TextField
        label="Item Make"
        name="itemMake"
        value={item.itemMake || ''}
        onChange={handleChange}
        fullWidth
        margin="normal"
        InputProps={{ style: textFieldStyle }}
      />
      <TextField
        label="Model"
        name="model"
        value={item.model || ''}
        onChange={handleChange}
        fullWidth
        margin="normal"
        InputProps={{ style: textFieldStyle }}
      />

<TextField
        label="Customer Name"
        name="customerName"
        value={item.customerName || ''}
        onChange={handleChange}
        fullWidth
        margin="normal"
        InputProps={{ style: textFieldStyle }}
      />

<TextField
        label="Product Name"
        name="productName"
        value={item.productName || ''}
        onChange={handleChange}
        fullWidth
        margin="normal"
        InputProps={{ style: textFieldStyle }}
      />

<TextField
        label="Purchase Date"
        name="purchaseDate"
        value={item.purchaseDate || ''}
        fullWidth
        margin="normal"
        inputProps={{ min: 0 }}
        InputProps={{ style: textFieldStyle }}
      />

<TextField
        label="Warranty Expiry Date"
        name="warrantyExpiryDate"
        value={item.warrantyExpiryDate || ''}
        fullWidth
        margin="normal"
        InputProps={{ style: textFieldStyle }}
      />

    </Container>
  );
}

export default EditForm;
